import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Theme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import * as React from 'react';
import { makeStyles } from 'tss-react/mui';
import useAsyncModifyApTemplate from '../../../../hooks/useAsyncModifyApTemplate';
import { ApTemplateType } from '../../../../sic-lib/enums/ApTemplateType';
import { RequestStatus } from '../../../../sic-lib/enums/RequestStatus';
import { GenericReducer, genericReducer } from '../../../../sic-lib/reducers/genericReducer';
import { ApTemplate, buildUpdateApTemplate } from '../../../../sic-lib/types/ApTemplate';
import { IErrorFormValidation } from '../../../../sic-lib/utils/formValidationHelpers';
import ButtonLoading from '../../../Shared/ButtonLoading';

const useStyles = makeStyles()((theme: Theme) => ({
    paper: {
        width: '100%',
    },
    text: {
        marginBottom: theme.spacing(3),
    },
}));

interface DialogState {
    title: string;
    formError: IErrorFormValidation | null;
}

interface Props {
    isOpen: boolean;
    onDoneHandler: (apTemplate?: ApTemplate) => void;
    apTemplate: ApTemplate;
}

export default function DialogCreateGroupedContacts({ isOpen, onDoneHandler, apTemplate }: Props) {
    const { classes } = useStyles();

    const { requestStatus, asyncModifyApTemplate } = useAsyncModifyApTemplate();

    const [dialogState, dialogDispatch] = React.useReducer<GenericReducer<DialogState>>(
        genericReducer,
        {
            title: '',
            formError: null,
        },
    );

    const handleChangeTitle = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        dialogDispatch({ title: event.target.value });
    };

    const handleClose = () => {
        onDoneHandler();
    };

    const handleSubmit = async () => {
        try {
            const updateObj = buildUpdateApTemplate({
                title: dialogState.title,
                type: ApTemplateType.client,
                status: apTemplate.status,
            });

            const asyncResult = await asyncModifyApTemplate(apTemplate.templateId, updateObj);

            if (asyncResult) {
                onDoneHandler({ ...apTemplate, ...updateObj });
            }
        } catch (error: any) {
            if (error.isFormError) {
                dialogDispatch({ formError: error as IErrorFormValidation });
            }
        }
    };

    return (
        <Dialog
            maxWidth="xs"
            aria-labelledby="dialog_save_client_ap_template"
            open={isOpen}
            PaperProps={{
                className: classes.paper,
            }}
        >
            <DialogTitle id="dialog_save_client_ap_template">Create Template</DialogTitle>
            <DialogContent dividers>
                <Typography variant="body1" className={classes.text}>
                    Enter a title for the Action Plan template
                </Typography>
                <TextField
                    id="contact_group_title"
                    label="Title"
                    name="title"
                    value={dialogState.title}
                    onChange={handleChangeTitle}
                    autoFocus
                    fullWidth
                    required
                    error={Boolean(dialogState.formError?.field === 'title')}
                    helperText={
                        dialogState.formError?.field === 'title'
                            ? dialogState.formError.message
                            : ''
                    }
                ></TextField>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Cancel</Button>
                <ButtonLoading
                    isLoading={requestStatus === RequestStatus.active}
                    onClick={handleSubmit}
                    color="secondary"
                    variant="outlined"
                >
                    Save
                </ButtonLoading>
            </DialogActions>
        </Dialog>
    );
}
